import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAutomationClient } from '../providers/AutomationProvider'
import { errorsKeys } from './useErrors'

/**
 * Query key factory for Linear-related queries
 */
export const linearKeys = {
  all: ['linear'] as const,
  status: () => [...linearKeys.all, 'status'] as const,
}

/**
 * Hook to check Linear integration status
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useLinearStatus()
 * if (data?.configured) {
 *   // Show Linear integration UI
 * }
 * ```
 */
export function useLinearStatus() {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: linearKeys.status(),
    queryFn: () => client.linear.status(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook to create a Linear issue from an error
 *
 * @example
 * ```tsx
 * const { mutate: createIssue, isPending } = useCreateLinearIssue()
 * createIssue('error-123', {
 *   onSuccess: (result) => window.open(result.issue.url)
 * })
 * ```
 */
export function useCreateLinearIssue() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (errorId: string) => client.linear.createIssue(errorId),
    onSuccess: (_data, errorId) => {
      // Update the error with the new Linear issue info
      queryClient.invalidateQueries({ queryKey: errorsKeys.detail(errorId) })
      queryClient.invalidateQueries({ queryKey: errorsKeys.lists() })
    },
  })
}
